import React, { createContext, useContext, useState, useEffect } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string, arabic: string, english: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

const translations = {
  ar: {
    'sign_up': 'إنشاء حساب',
    'sign_in': 'تسجيل الدخول',
    'sign_in_success': 'تم تسجيل الدخول بنجاح',
    'sign_in_error': 'خطأ في تسجيل الدخول',
    'signing_in': 'جاري تسجيل الدخول...',
    'email': 'البريد الإلكتروني',
    'password': 'كلمة المرور',
    'no_account': 'ليس لديك حساب؟',
    'create_account': 'إنشاء حساب',
    'choose_account_type': 'اختر نوع الحساب',
    'complete_registration': 'أكمل التسجيل',
    'student': 'طالب',
    'landlord': 'مالك العقار',
    'continue_with_role': 'المتابعة كـ',
    'full_name': 'الاسم الكامل',
    'gender': 'الجنس',
    'university': 'الجامعة',
    'phone': 'رقم الهاتف',
    'national_id': 'الرقم القومي',
    'male': 'ذكر',
    'female': 'أنثى',
    'other': 'أخرى',
    'select_gender': 'اختر الجنس',
    'select_university': 'اختر الجامعة',
    'enter_full_name': 'أدخل الاسم الكامل',
    'enter_phone': 'أدخل رقم الهاتف',
    'enter_national_id': 'أدخل الرقم القومي',
    'already_have_account': 'لديك حساب بالفعل؟',
    'reset_password': 'إعادة تعيين كلمة المرور',
    'reset_password_description': 'أدخل بريدك الإلكتروني لإعادة تعيين كلمة المرور',
    'send_reset_link': 'إرسال رابط إعادة التعيين',
    'back_to_login': 'العودة إلى تسجيل الدخول',
    'email_verification_required': 'التحقق من البريد الإلكتروني مطلوب',
    'email_verification_description': 'يرجى التحقق من بريدك الإلكتروني لتفعيل حسابك',
    'resend_verification_email': 'إعادة إرسال بريد التحقق',
    'listings': 'العقارات',
    'available_listings': 'العقارات المتاحة',
    'browse_student_housing': 'تصفح خيارات السكن الطلابي',
    'search_location': 'ابحث عن موقع',
    'search': 'بحث',
    'features': 'الميزات',
    'about_us': 'من نحن',
    'contact_us': 'اتصل بنا',
    'privacy_policy': 'سياسة الخصوصية',
    'terms_of_service': 'شروط الخدمة',
    'compare': 'مقارنة',
    'dashboard': 'لوحة التحكم',
    'account': 'الحساب',
    'list_property': 'أضف عقارك',
    'my_listings': 'عقاراتي',
    'wishlist': 'المفضلة',
    'admin_dashboard': 'لوحة تحكم المسؤول',
    'user_management': 'إدارة المستخدمين',
    'kyc_management': 'إدارة التحقق من الهوية',
    'listings_management': 'إدارة العقارات',
    'universities_management': 'إدارة الجامعات',
    'admin_settings': 'إعدادات المسؤول',
    'security_dashboard': 'لوحة معلومات الأمان',
    'not_found': 'غير موجود',
    'page_not_found': 'الصفحة غير موجودة',
    'back_to_home': 'العودة إلى الرئيسية',
    'maskan': 'مسكن',
    'hero_subtitle': 'سكن طلابي موثوق في مصر',
    'find_housing': 'ابحث عن سكن',
    'verified_100': 'متحقق 100%',
    'students_count': '+10,000 طالب',
    'universities_count': '+50 جامعة',
    'verified_listings': 'قوائم متحققة',
    'verified_listings_desc': 'جميع العقارات والملاك متحققون',
    'university_focused': 'مرتكز على الجامعات',
    'university_focused_desc': 'ابحث حسب المسافة من الحرم الجامعي',
    'student_community': 'مجتمع طلابي',
    'student_community_desc': 'سكن آمن للطلاب',
    'quick_response': 'استجابة سريعة',
    'quick_response_desc': 'استجابة سريعة من المالكين',
    'something_went_wrong': 'حدث خطأ ما',
    'error_loading_application': 'حدث خطأ أثناء تحميل التطبيق',
    'reload_page': 'إعادة تحميل الصفحة',
    'sign_in_description': 'أدخل بياناتك لتسجيل الدخول',
    'email_readonly': 'لا يمكن تغيير البريد الإلكتروني',
    'profile_info': 'المعلومات الشخصية',
    'edit': 'تعديل',
    'cancel': 'إلغاء',
    'save': 'حفظ',
    'saving': 'جاري الحفظ',
    'not_set': 'غير محدد',
    'update_failed': 'فشل في التحديث',
    'profile_updated': 'تم تحديث الملف الشخصي',
    'arabic': 'العربية',
    'english': 'الإنجليزية',
    'language': 'اللغة',
    'select_language': 'اختر اللغة',
    'update_profile': 'تحديث الملف الشخصي',
    'sign_out': 'تسجيل الخروج',
    'signed_out_successfully': 'تم تسجيل الخروج بنجاح',
    'list_new_property': 'إضافة عقار جديد',
    'property_details': 'تفاصيل العقار',
    'property_location': 'موقع العقار',
    'property_photos': 'صور العقار',
    'submit': 'إرسال',
    'enter_address': 'أدخل العنوان',
    'select_city': 'اختر المدينة',
    'select_area': 'اختر المنطقة',
    'enter_street': 'أدخل الشارع',
    'enter_building': 'أدخل رقم المبنى',
    'enter_apartment': 'أدخل رقم الشقة',
    'price_per_month': 'السعر الشهري',
    'deposit_amount': 'مبلغ التأمين',
    'distance_to_university': 'المسافة إلى الجامعة',
    'unit_type': 'نوع الوحدة',
    'gender_rules': 'قواعد الجنس',
    'furnished': 'مفروش',
    'available_from': 'متاح من',
    'lease_duration': 'مدة الإيجار',
    'amenities': 'المرافق',
    'description': 'الوصف',
    'upload_photos': 'تحميل الصور',
    'address': 'العنوان',
    'city': 'المدينة',
    'area': 'المنطقة',
    'street': 'الشارع',
    'building': 'المبنى',
    'apartment': 'الشقة',
    'price': 'السعر',
    'deposit': 'التأمين',
    'distance': 'المسافة',
    'type': 'النوع',
    'rules': 'القواعد',
    'duration': 'المدة',
    'add_amenity': 'أضف مرفق',
    'enter_amenity': 'أدخل مرفق',
    'add': 'أضف',
    'submit_listing': 'إرسال العقار',
    'listing_submitted': 'تم إرسال العقار',
    'listing_updated': 'تم تحديث العقار',
    'update_listing': 'تحديث العقار',
    'delete_listing': 'حذف العقار',
    'confirm_delete_listing': 'هل أنت متأكد أنك تريد حذف هذا العقار؟',
    'yes': 'نعم',
    'no': 'لا',
    'listing_deleted': 'تم حذف العقار',
    'edit_listing': 'تعديل العقار',
    'view_listing': 'عرض العقار',
    'no_listings_found': 'لا توجد عقارات متاحة',
    'add_to_wishlist': 'أضف إلى المفضلة',
    'remove_from_wishlist': 'إزالة من المفضلة',
    'added_to_wishlist': 'تمت الإضافة إلى المفضلة',
    'removed_from_wishlist': 'تمت الإزالة من المفضلة',
    'no_wishlist_items': 'لا توجد عناصر في المفضلة',
    'my_wishlist': 'المفضلة',
    'profile_completion': 'إكمال الملف الشخصي',
    'complete_profile_first': 'يرجى إكمال ملفك الشخصي أولاً',
    'complete_now': 'إكمال الآن',
    'profile_incomplete': 'الملف الشخصي غير مكتمل',
    'kyc_required': 'التحقق من الهوية مطلوب',
    'verify_identity': 'تحقق من هويتك',
    'upload_id_front': 'تحميل صورة الهوية الأمامية',
    'upload_id_back': 'تحميل صورة الهوية الخلفية',
    'upload': 'تحميل',
    'id_front': 'صورة الهوية الأمامية',
    'id_back': 'صورة الهوية الخلفية',
    'kyc_submitted': 'تم إرسال طلب التحقق من الهوية',
    'kyc_pending': 'التحقق من الهوية معلق',
    'kyc_approved': 'تم التحقق من الهوية',
    'kyc_rejected': 'تم رفض التحقق من الهوية',
    'kyc_status': 'حالة التحقق من الهوية',
    'upload_id': 'تحميل الهوية',
    'back_to_website': 'العودة للموقع',
    'admin': 'مسؤول',
    'superadmin': 'مشرف',
    'role': 'الدور',
    'users': 'المستخدمين',
    'kyc': 'التحقق من الهوية',
    'listings_approval': 'الموافقة على العقارات',
    'universities': 'الجامعات',
    'settings': 'الإعدادات',
    'security': 'الأمان',
    'create_new_university': 'إنشاء جامعة جديدة',
    'university_name_en': 'اسم الجامعة بالإنجليزية',
    'university_name_ar': 'اسم الجامعة بالعربية',
    'university_city': 'مدينة الجامعة',
    'enter_university_name_en': 'أدخل اسم الجامعة بالإنجليزية',
    'enter_university_name_ar': 'أدخل اسم الجامعة بالعربية',
    'enter_university_city': 'أدخل مدينة الجامعة',
    'create': 'إنشاء',
    'university_created': 'تم إنشاء الجامعة',
    'university_updated': 'تم تحديث الجامعة',
    'university_deleted': 'تم حذف الجامعة',
    'edit_university': 'تعديل الجامعة',
    'delete_university': 'حذف الجامعة',
    'confirm_delete_university': 'هل أنت متأكد أنك تريد حذف هذه الجامعة؟',
    'pending': 'معلق',
    'approved': 'موافق عليه',
    'rejected': 'مرفوض',
    'approve': 'موافقة',
    'reject': 'رفض',
    'reason_for_rejection': 'سبب الرفض',
    'enter_reason_for_rejection': 'أدخل سبب الرفض',
    'listing_approved_successfully': 'تمت الموافقة على العقار بنجاح',
    'listing_rejected_successfully': 'تم رفض العقار بنجاح',
    'listings_awaiting_approval': 'العقارات في انتظار الموافقة',
    'no_listings_awaiting_approval': 'لا توجد عقارات في انتظار الموافقة',
    'all': 'الكل',
    'active': 'نشط',
    'inactive': 'غير نشط',
    'status': 'الحالة',
    'set_active': 'تفعيل',
    'set_inactive': 'تعطيل',
    'user': 'مستخدم',
    'admin_role': 'مسؤول',
    'superadmin_role': 'مشرف',
    'set_role': 'تحديد الدور',
    'user_updated_successfully': 'تم تحديث المستخدم بنجاح',
    'users_list': 'قائمة المستخدمين',
    'no_users_found': 'لا يوجد مستخدمين',
    'edit_user': 'تعديل المستخدم',
    'delete_user': 'حذف المستخدم',
    'confirm_delete_user': 'هل أنت متأكد أنك تريد حذف هذا المستخدم؟',
    'user_deleted_successfully': 'تم حذف المستخدم بنجاح',
    'kyc_list': 'قائمة التحقق من الهوية',
    'no_kyc_requests_found': 'لا توجد طلبات تحقق من الهوية',
    'view_details': 'عرض التفاصيل',
    'date': 'التاريخ',
    'log_out': 'تسجيل الخروج',
    'activity_feed': 'سجل النشاطات',
    'queue_snapshot': 'نظرة عامة على قائمة الانتظار',
    'kpi_cards': 'بطاقات مؤشرات الأداء الرئيسية',
    'analytics_overview': 'نظرة عامة على التحليلات',
    'trends_insights': 'اتجاهات ورؤى النشاط',
    'booking_status': 'حالة الحجز',
    'listing_status': 'حالة القائمة',
    '7_days': '7 أيام',
    '30_days': '30 يوماً',
    '90_days': '90 يوماً',
    'line': 'خط',
    'bar': 'شريط',
    'pick_date_range': 'اختر نطاق التاريخ',
    'personalized_based_on_preferences': 'مخصص حسب تفضيلاتك',
    'match': 'مطابقة',
    'view_all_recommendations': 'عرض جميع التوصيات',
    'recommended_listings': 'العقارات الموصى بها',
    'personalized_recommendations_desc': 'توصيات مخصصة حسب تفضيلاتك',
    'property_comparison': 'مقارنة العقارات',
    'add_more_properties': 'أضف المزيد من العقارات للمقارنة',
    'can_compare_up_to': 'يمكنك مقارنة حتى',
    'properties': 'عقارات',
    'max_comparison_reached': 'يمكنك مقارنة 3 عقارات كحد أقصى',
    'already_in_comparison': 'هذا العقار موجود بالفعل في المقارنة',
    'added_to_comparison': 'تم إضافة العقار للمقارنة',
    'removed_from_comparison': 'تم حذف العقار من المقارنة',
    'need_two_properties': 'تحتاج على الأقل عقارين للمقارنة',
    'min_lease': 'أقل مدة إيجار',
    'available': 'متاح',
    'month': 'شهر',
    'months': 'شهور',
    'egp': 'ج.م',
    'verified': 'موثق',
    'sign_in_to_view': 'يجب تسجيل الدخول لمشاهدة العقار',
    'sign_in_view_desc': 'سجل دخولك للوصول إلى تفاصيل العقار والتواصل مع المالك',
    'sign_in_to_list': 'يجب تسجيل الدخول لإدراج عقار',
    'sign_in_list_desc': 'أنشئ حساباً أو سجل دخولك لإدراج عقارك',
    'sign_in_required': 'يجب تسجيل الدخول',
    'sign_in_required_desc': 'سجل دخولك للوصول إلى هذه الصفحة',
    'recommended_for_you': 'مُوصى لك',
    'personalized_recommendations': 'عقارات مُختارة بعناية بناءً على جامعتك وتفضيلاتك الشخصية',
    'view_all_properties': 'عرض جميع العقارات',
    'no_recommendations': 'لا توجد توصيات متاحة حالياً',
    'browse_all_listings': 'تصفح جميع القوائم',
    'key_information': 'المعلومات الأساسية',
    'minimum_lease': 'أقل مدة إيجار',
    'amenities_features': 'المرافق والخصائص',
    'additional_resources': 'موارد إضافية',
    'property_video': 'فيديو العقار',
    'floor_plan': 'مخطط الطابق',
    'property_owner': 'مالك العقار',
    'response_rate_info': '95% معدل الاستجابة',
    'available_now': 'متاح الآن',
    'roommates_allowed': 'مسموح شركاء السكن',
    'call_owner': 'اتصل بالمالك',
    'phone_not_available': 'رقم الهاتف غير متوفر',
    'ui_content_settings': 'إعدادات الواجهة والمحتوى',
    'ui_content_description': 'تحكم في عناصر الواجهة والمحتوى المعروض للمستخدمين',
    'show_recommended_section': 'عرض قسم الموصى لك',
    'show_recommended_section_desc': 'عرض قسم "الموصى لك" في الصفحة الرئيسية للمستخدمين المسجلين'
  },
  en: {
    'sign_up': 'Sign Up',
    'sign_in': 'Sign In',
    'sign_in_success': 'Signed in successfully',
    'sign_in_error': 'Error signing in',
    'signing_in': 'Signing in...',
    'email': 'Email',
    'password': 'Password',
    'no_account': "Don't have an account?",
    'create_account': 'Create Account',
    'choose_account_type': 'Choose your account type',
    'complete_registration': 'Complete your registration',
    'student': 'Student',
    'landlord': 'Landlord',
    'continue_with_role': 'Continue as',
    'full_name': 'Full Name',
    'gender': 'Gender',
    'university': 'University',
    'phone': 'Phone Number',
    'national_id': 'National ID',
    'male': 'Male',
    'female': 'Female',
    'other': 'Other',
    'select_gender': 'Select Gender',
    'select_university': 'Select University',
    'enter_full_name': 'Enter full name',
    'enter_phone': 'Enter phone number',
    'enter_national_id': 'Enter national ID',
    'already_have_account': 'Already have an account?',
    'reset_password': 'Reset Password',
    'reset_password_description': 'Enter your email to reset your password',
    'send_reset_link': 'Send Reset Link',
    'back_to_login': 'Back to Login',
    'email_verification_required': 'Email Verification Required',
    'email_verification_description': 'Please verify your email to activate your account',
    'resend_verification_email': 'Resend Verification Email',
    'listings': 'Listings',
    'available_listings': 'Available Listings',
    'browse_student_housing': 'Browse student housing options',
    'search_location': 'Search Location',
    'search': 'Search',
    'features': 'Features',
    'about_us': 'About Us',
    'contact_us': 'Contact Us',
    'privacy_policy': 'Privacy Policy',
    'terms_of_service': 'Terms of Service',
    'compare': 'Compare',
    'dashboard': 'Dashboard',
    'account': 'Account',
    'list_property': 'List Property',
    'my_listings': 'My Listings',
    'wishlist': 'Wishlist',
    'admin_dashboard': 'Admin Dashboard',
    'user_management': 'User Management',
    'kyc_management': 'KYC Management',
    'listings_management': 'Listings Management',
    'universities_management': 'Universities Management',
    'admin_settings': 'Admin Settings',
    'security_dashboard': 'Security Dashboard',
    'not_found': 'Not Found',
    'page_not_found': 'Page Not Found',
    'back_to_home': 'Back to Home',
    'maskan': 'Maskan',
    'hero_subtitle': 'Trusted Student Housing in Egypt',
    'find_housing': 'Find Housing',
    'verified_100': '100% Verified',
    'students_count': '10,000+ Students',
    'universities_count': '50+ Universities',
    'verified_listings': 'Verified Listings',
    'verified_listings_desc': 'All properties and owners verified',
    'university_focused': 'University Focused',
    'university_focused_desc': 'Search by distance from campus',
    'student_community': 'Student Community',
    'student_community_desc': 'Safe housing for students',
    'quick_response': 'Quick Response',
    'quick_response_desc': 'Fast owner responses',
    'something_went_wrong': 'Something went wrong',
    'error_loading_application': 'An error occurred while loading the application',
    'reload_page': 'Reload Page',
    'sign_in_description': 'Enter your credentials to sign in',
    'email_readonly': 'Email cannot be changed',
    'profile_info': 'Profile Information',
    'edit': 'Edit',
    'cancel': 'Cancel',
    'save': 'Save',
    'saving': 'Saving...',
    'not_set': 'Not set',
    'update_failed': 'Update failed',
    'profile_updated': 'Profile updated successfully',
    'arabic': 'Arabic',
    'english': 'English',
    'language': 'Language',
    'select_language': 'Select Language',
    'update_profile': 'Update Profile',
    'sign_out': 'Sign out',
    'signed_out_successfully': 'Signed out successfully',
    'list_new_property': 'List New Property',
    'property_details': 'Property Details',
    'property_location': 'Property Location',
    'property_photos': 'Property Photos',
    'submit': 'Submit',
    'enter_address': 'Enter Address',
    'select_city': 'Select City',
    'select_area': 'Select Area',
    'enter_street': 'Enter Street',
    'enter_building': 'Enter Building Number',
    'enter_apartment': 'Enter Apartment Number',
    'price_per_month': 'Price Per Month',
    'deposit_amount': 'Deposit Amount',
    'distance_to_university': 'Distance to University',
    'unit_type': 'Unit Type',
    'gender_rules': 'Gender Rules',
    'furnished': 'Furnished',
    'available_from': 'Available From',
    'lease_duration': 'Lease Duration',
    'amenities': 'Amenities',
    'description': 'Description',
    'upload_photos': 'Upload Photos',
    'address': 'Address',
    'city': 'City',
    'area': 'Area',
    'street': 'Street',
    'building': 'Building',
    'apartment': 'Apartment',
    'price': 'Price',
    'deposit': 'Deposit',
    'distance': 'Distance',
    'type': 'Type',
    'rules': 'Rules',
    'duration': 'Duration',
    'add_amenity': 'Add Amenity',
    'enter_amenity': 'Enter Amenity',
    'add': 'Add',
    'submit_listing': 'Submit Listing',
    'listing_submitted': 'Listing Submitted',
    'listing_updated': 'Listing Updated',
    'update_listing': 'Update Listing',
    'delete_listing': 'Delete Listing',
    'confirm_delete_listing': 'Are you sure you want to delete this listing?',
    'yes': 'Yes',
    'no': 'No',
    'listing_deleted': 'Listing Deleted',
    'edit_listing': 'Edit Listing',
    'view_listing': 'View Listing',
    'no_listings_found': 'No listings found',
    'add_to_wishlist': 'Add to Wishlist',
    'remove_from_wishlist': 'Remove from Wishlist',
    'added_to_wishlist': 'Added to Wishlist',
    'removed_from_wishlist': 'Removed from Wishlist',
    'no_wishlist_items': 'No items in wishlist',
    'my_wishlist': 'My Wishlist',
    'profile_completion': 'Profile Completion',
    'complete_profile_first': 'Please complete your profile first',
    'complete_now': 'Complete Now',
    'profile_incomplete': 'Profile Incomplete',
    'kyc_required': 'KYC Required',
    'verify_identity': 'Verify Identity',
    'upload_id_front': 'Upload ID Front',
    'upload_id_back': 'Upload ID Back',
    'upload': 'Upload',
    'id_front': 'ID Front',
    'id_back': 'ID Back',
    'kyc_submitted': 'KYC Submitted',
    'kyc_pending': 'KYC Pending',
    'kyc_approved': 'KYC Approved',
    'kyc_rejected': 'KYC Rejected',
    'kyc_status': 'KYC Status',
    'upload_id': 'Upload ID',
    'back_to_website': 'Back to Website',
    'admin': 'Admin',
    'superadmin': 'Superadmin',
    'role': 'Role',
    'users': 'Users',
    'kyc': 'KYC',
    'listings_approval': 'Listings Approval',
    'universities': 'Universities',
    'settings': 'Settings',
    'security': 'Security',
    'create_new_university': 'Create New University',
    'university_name_en': 'University Name (EN)',
    'university_name_ar': 'University Name (AR)',
    'university_city': 'University City',
    'enter_university_name_en': 'Enter University Name (EN)',
    'enter_university_name_ar': 'Enter University Name (AR)',
    'enter_university_city': 'Enter University City',
    'create': 'Create',
    'university_created': 'University Created',
    'university_updated': 'University Updated',
    'university_deleted': 'University Deleted',
    'edit_university': 'Edit University',
    'delete_university': 'Delete University',
    'confirm_delete_university': 'Are you sure you want to delete this university?',
    'pending': 'Pending',
    'approved': 'Approved',
    'rejected': 'Rejected',
    'approve': 'Approve',
    'reject': 'Reject',
    'reason_for_rejection': 'Reason for Rejection',
    'enter_reason_for_rejection': 'Enter Reason for Rejection',
    'listing_approved_successfully': 'Listing Approved Successfully',
    'listing_rejected_successfully': 'Listing Rejected Successfully',
    'listings_awaiting_approval': 'Listings Awaiting Approval',
    'no_listings_awaiting_approval': 'No Listings Awaiting Approval',
    'all': 'All',
    'active': 'Active',
    'inactive': 'Inactive',
    'status': 'Status',
    'set_active': 'Set Active',
    'set_inactive': 'Set Inactive',
    'user': 'User',
    'admin_role': 'Admin',
    'superadmin_role': 'Superadmin',
    'set_role': 'Set Role',
    'user_updated_successfully': 'User Updated Successfully',
    'users_list': 'Users List',
    'no_users_found': 'No Users Found',
    'edit_user': 'Edit User',
    'delete_user': 'Delete User',
    'confirm_delete_user': 'Are you sure you want to delete this user?',
    'user_deleted_successfully': 'User Deleted Successfully',
    'kyc_list': 'KYC List',
    'no_kyc_requests_found': 'No KYC Requests Found',
    'view_details': 'View Details',
    'date': 'Date',
    'log_out': 'Log Out',
    'activity_feed': 'Activity Feed',
    'queue_snapshot': 'Queue Snapshot',
    'kpi_cards': 'KPI Cards',
    'analytics_overview': 'Analytics Overview',
    'trends_insights': 'Activity trends and insights',
    'booking_status': 'Booking Status',
    'listing_status': 'Listing Status',
    '7_days': '7 Days',
    '30_days': '30 Days',
    '90_days': '90 Days',
    'line': 'Line',
    'bar': 'Bar',
    'pick_date_range': 'Pick a date range',
    'personalized_based_on_preferences': 'Personalized based on your preferences',
    'match': 'match',
    'view_all_recommendations': 'View All Recommendations',
    'recommended_listings': 'Recommended Listings',
    'personalized_recommendations_desc': 'Personalized recommendations based on your preferences',
    'property_comparison': 'Property Comparison',
    'add_more_properties': 'Add more properties to compare',
    'can_compare_up_to': 'You can compare up to',
    'properties': 'properties',
    'max_comparison_reached': 'You can compare up to 3 properties',
    'already_in_comparison': 'Property already in comparison',
    'added_to_comparison': 'Property added to comparison',
    'removed_from_comparison': 'Property removed from comparison',
    'need_two_properties': 'You need at least 2 properties to compare',
    'min_lease': 'Min. lease',
    'available': 'Available',
    'month': 'month',
    'months': 'months',
    'egp': 'EGP',
    'verified': 'Verified',
    'sign_in_to_view': 'Please sign in to view this listing',
    'sign_in_view_desc': 'Sign in to access listing details and contact the owner',
    'sign_in_to_list': 'Please sign in to list a property',
    'sign_in_list_desc': 'Create an account or sign in to list your property',
    'sign_in_required': 'Please sign in to continue',
    'sign_in_required_desc': 'Sign in to access this page',
    'recommended_for_you': 'Recommended For You',
    'personalized_recommendations': 'Carefully selected properties based on your university and personal preferences',
    'view_all_properties': 'View All Properties',
    'no_recommendations': 'No recommendations available at the moment',
    'browse_all_listings': 'Browse All Listings',
    'key_information': 'Key Information',
    'minimum_lease': 'Minimum Lease',
    'amenities_features': 'Amenities & Features',
    'additional_resources': 'Additional Resources',
    'property_video': 'Property Video',
    'floor_plan': 'Floor Plan',
    'property_owner': 'Property Owner',
    'response_rate_info': '95% response rate',
    'available_now': 'Available Now',
    'roommates_allowed': 'Roommates Allowed',
    'call_owner': 'Call Owner',
    'phone_not_available': 'Phone number not available',
    'ui_content_settings': 'UI & Content Settings',
    'ui_content_description': 'Control interface elements and content displayed to users',
    'show_recommended_section': 'Show Recommended Section',
    'show_recommended_section_desc': 'Display the "Recommended For You" section on the homepage for logged-in users'
  }
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState(localStorage.getItem('lang') || 'ar');

  useEffect(() => {
    localStorage.setItem('lang', language);
    document.documentElement.setAttribute('lang', language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key: string, arabic: string, english: string): string => {
    return translations[language as keyof typeof translations]?.[key] || english;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
